/**
 * LotuSun Phase 0 — Interaction Layer
 *
 * Sigil: "Build the temple, not the storefront. The merchant edits ritual;
 *        the system refuses to drift."
 *
 * Binding contract: .ledge/decisions/G5-motion-architecture-spec.md
 *
 * Three self-contained init functions, each guarded by feature detection.
 * No framework. No build step. Vanilla only.
 *
 * Scope: all behavior is scoped to LotuSun elements via explicit selectors.
 * Horizon globals are untouched.
 *
 * Class contract (aligned to assets/lotusun-motion.css):
 *   - Reveal gating: html.lu-reveal-ready (set by inline <head> snippet before paint)
 *     CSS hides: html.lu-reveal-ready [data-lotusun-reveal]:not(.is-revealed)
 *     JS adds:   .is-revealed on intersect (once, then unobserve)
 *     JS does NOT add .is-hidden — the CSS gating class handles hiding.
 *   - Accordion: details.lu-accordion-enhanced + .lu-accordion-body inner wrapper
 *     CSS drives open/close via native [open] attribute + grid-template-rows 0fr↔1fr.
 *     JS intercepts close only (to allow CSS transition before [open] removal).
 *   - Gallery: .lotusun-buy-box__main-img.is-swapping (unchanged — already correct)
 *   - Nav bag: .cart-bubble.is-bumped (added/removed by this module on cart update)
 *
 * Failure-safety:
 *   - initScrollReveal: lu-reveal-ready is added by the inline <head> script only
 *     when motion is allowed AND IntersectionObserver is present. If the inline
 *     script never ran (no-JS), the class is absent — CSS never hides anything.
 *     This module only adds .is-revealed; it never hides elements.
 *   - initGallery: thumb <button>s are inert without JS. Main image is the
 *     Liquid-rendered featured image. No broken state.
 *   - initAccordion: native <details>/<summary> is fully functional without JS.
 *     JS adds .lu-accordion-enhanced + .lu-accordion-body wrapper only after
 *     confirming capability. No-JS = snap open/close (native behavior).
 *   - initCart: if JS fails, the native {% form 'product' %} POST to /cart/add
 *     still works — the form has no data-ajax requirement.
 *
 * Cart event wiring:
 *   CartAddEvent.eventName = ThemeEvents.cartUpdate = 'cart:update'
 *   (confirmed via assets/events.js:115 and assets/cart-icon.js:30)
 *   Horizon's CartDrawerComponent listens for 'cart:update' and calls
 *   showDialog() when auto-open attribute is present.
 *   Horizon's CartIcon listens for 'cart:update' and updates the count bubble
 *   + fires the cart-bubble--animating pulse keyframe automatically.
 */

// ---------------------------------------------------------------------------
// 1. SCROLL REVEAL — IntersectionObserver on [data-lotusun-reveal]
// ---------------------------------------------------------------------------

/**
 * Reduced-motion guard. Returns true if the user prefers reduced motion.
 * @returns {boolean}
 */
function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia != null &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * Assign --reveal-index to each direct child of a stagger container.
 * Stagger containers carry data-lotusun-stagger on the parent.
 * The CSS uses transition-delay: calc(var(--reveal-index, 0) * 30ms).
 * @param {Element} el
 */
function assignStaggerIndices(el) {
  const staggerContainer = el.querySelector('[data-lotusun-stagger]');
  if (!staggerContainer) return;
  Array.from(staggerContainer.children).forEach((child, i) => {
    /** @type {HTMLElement} */ (child).style.setProperty('--reveal-index', String(i));
  });
}

/**
 * Section-enter scroll reveal.
 *
 * Per spec §1 row 1:
 *   - opacity 0→1, translateY(12px→0), ease-out, 400ms ceiling
 *   - stagger 30ms per grid child via --reveal-index
 *   - fires once then unobserves
 *   - threshold: 0.12
 *
 * Safety rule: the inline <head> script is the only gating point. It adds
 * html.lu-reveal-ready only when motion is allowed AND IO is present.
 * This module never hides elements — it only adds .is-revealed on intersect.
 * If html.lu-reveal-ready is absent (no-JS or reduced-motion path), the CSS
 * rule that sets opacity:0 never applies — all content remains visible.
 *
 * Failure safety (path-4b): if anything throws AFTER lu-reveal-ready is set
 * (observer instantiation or observe loop), revealAll() ensures no element
 * is left hidden with opacity:0. The try/catch closes that gap.
 */

/**
 * Unconditionally reveal all [data-lotusun-reveal] elements.
 * Called when the observer path fails after lu-reveal-ready is already set.
 */
function revealAll() {
  document.querySelectorAll('[data-lotusun-reveal]').forEach((el) => {
    el.classList.add('is-revealed');
  });
}

function initScrollReveal() {
  // If the inline head script did not add lu-reveal-ready, motion is disabled
  // or IO is unsupported — nothing to do. Elements are already visible.
  if (!document.documentElement.classList.contains('lu-reveal-ready')) return;

  const elements = document.querySelectorAll('[data-lotusun-reveal]');
  if (!elements.length) return;

  // Assign stagger indices before observer fires (so layout is stable)
  elements.forEach(assignStaggerIndices);

  try {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          // Add .is-revealed — CSS transitions opacity 0→1 and translateY 12px→0
          el.classList.add('is-revealed');
          observer.unobserve(el);
        });
      },
      { threshold: 0.12 }
    );

    elements.forEach((el) => {
      observer.observe(el);
    });
  } catch (_err) {
    // Observer instantiation or observe loop threw after lu-reveal-ready was set.
    // Reveal everything immediately so no element is left hidden.
    revealAll();
  }
}

// ---------------------------------------------------------------------------
// 2. PDP GALLERY THUMB SWAP
// ---------------------------------------------------------------------------

/**
 * Gallery thumb swap.
 *
 * Per spec §1 row 2:
 *   - click thumbnail → swap main image src/srcset
 *   - update aria-current on thumbs (single-select rail — Class A behavioral contract)
 *     aria-current="true" on active; attribute absent on inactive.
 *     aria-pressed was incorrect: it models N independent toggles, not one-of-N selection.
 *   - update image counter (#lotusun-img-current)
 *   - 160ms opacity crossfade via .is-swapping class on .lotusun-buy-box__main-img
 *     (CSS contract: .lotusun-buy-box__main-img.is-swapping { opacity: 0 })
 *   - interruptible: new click mid-fade replaces immediately
 *
 * Keyboard: thumbs are <button type="button"> — Tab-reachable and Enter/Space
 * operable by default. Arrow keys (Up/Down/Left/Right + Home/End) move focus
 * along the rail and select (selection-follows-focus), per the Class A
 * SELECTION-RAIL contract. Plain buttons → no roving tabindex required.
 *
 * Reduced-motion: CSS removes the transition under prefers-reduced-motion;
 * the DOM update still fires. No JS branch needed — CSS owns the visual path.
 */
function initGallery() {
  const thumbsContainer = document.querySelector('.lotusun-buy-box__thumbs');
  if (!thumbsContainer) return;

  const mainImageWrapper = document.getElementById('lotusun-main-image');
  if (!mainImageWrapper) return;

  // Total image count — read from Liquid-rendered live region initial text
  // e.g. "Image 1 of 5" → total = 5
  const liveRegion = document.getElementById('lotusun-img-counter-live');
  const total = liveRegion
    ? parseInt(liveRegion.textContent?.replace(/.*of\s*/, '') ?? '0', 10) || 0
    : 0;

  /**
   * @param {HTMLButtonElement} thumb
   */
  function activateThumb(thumb) {
    const mainImg = mainImageWrapper.querySelector('.lotusun-buy-box__main-img');
    const counter = document.getElementById('lotusun-img-current');
    const allThumbs = /** @type {NodeListOf<HTMLButtonElement>} */ (
      thumbsContainer.querySelectorAll('.lotusun-buy-box__thumb[data-image-index]')
    );
    const newSrc = thumb.dataset.imageSrc;
    const newAlt = thumb.dataset.imageAlt || '';
    const newIndex = parseInt(thumb.dataset.imageIndex ?? '0', 10);

    // Update aria-current on all thumbs (single-select rail — Class A behavioral contract).
    // aria-current="true" on active; attribute absent on inactive (aria-current="false" is invalid).
    allThumbs.forEach((t) => {
      t.removeAttribute('aria-current');
      t.classList.remove('lotusun-buy-box__thumb--active');
    });
    thumb.setAttribute('aria-current', 'true');
    thumb.classList.add('lotusun-buy-box__thumb--active');

    // Update visual counter (aria-hidden — for sighted users only)
    if (counter) {
      counter.textContent = String(newIndex + 1);
    }

    // Update screen-reader live region with full "Image N of X" string
    if (liveRegion && total > 0) {
      liveRegion.textContent = `Image ${newIndex + 1} of ${total}`;
    }

    // Crossfade: add .is-swapping (CSS opacity:0 transition 160ms), then swap src.
    // DEFECT-5 fix: clear srcset BEFORE writing src. Browsers prioritize srcset over
    // src when both are present — setting only src while srcset still references the
    // original image leaves the displayed image unchanged. Clearing srcset first
    // forces the browser to use the plain src value we are about to write.
    // Remove .is-swapping after 180ms to allow future swaps cleanly.
    if (mainImg instanceof HTMLImageElement && newSrc) {
      mainImg.classList.add('is-swapping');
      mainImg.removeAttribute('srcset');
      mainImg.removeAttribute('sizes');
      mainImg.src = newSrc;
      if (newAlt) mainImg.alt = newAlt;
      setTimeout(() => {
        mainImg.classList.remove('is-swapping');
      }, 180);
    }
  }

  // Event delegation on the thumbs container (handles dynamic content safely)
  thumbsContainer.addEventListener('click', (event) => {
    const thumb = /** @type {HTMLElement} */ (event.target).closest(
      '.lotusun-buy-box__thumb[data-image-index]'
    );
    if (!(thumb instanceof HTMLButtonElement)) return;
    activateThumb(thumb);
  });

  // Arrow-key navigation across the rail (Class A SELECTION-RAIL target pattern).
  // Arrows move focus to the adjacent thumb and select it (selection-follows-focus,
  // like a single-select listbox). Plain <button>s stay in the Tab order — no
  // roving tabindex. Home/End jump to the ends; the rail wraps at both edges.
  thumbsContainer.addEventListener('keydown', (event) => {
    const { key } = event;
    const isNav =
      key === 'ArrowDown' ||
      key === 'ArrowRight' ||
      key === 'ArrowUp' ||
      key === 'ArrowLeft' ||
      key === 'Home' ||
      key === 'End';
    if (!isNav) return;

    const thumbs = /** @type {HTMLButtonElement[]} */ (
      Array.from(thumbsContainer.querySelectorAll('.lotusun-buy-box__thumb[data-image-index]'))
    );
    if (thumbs.length === 0) return;

    const currentIndex = thumbs.indexOf(/** @type {HTMLButtonElement} */ (document.activeElement));
    if (currentIndex === -1) return;

    let nextIndex = currentIndex;
    if (key === 'ArrowDown' || key === 'ArrowRight') {
      nextIndex = (currentIndex + 1) % thumbs.length;
    } else if (key === 'ArrowUp' || key === 'ArrowLeft') {
      nextIndex = (currentIndex - 1 + thumbs.length) % thumbs.length;
    } else if (key === 'Home') {
      nextIndex = 0;
    } else if (key === 'End') {
      nextIndex = thumbs.length - 1;
    }

    event.preventDefault();
    const nextThumb = thumbs[nextIndex];
    nextThumb.focus();
    activateThumb(nextThumb);
  });
}

// ---------------------------------------------------------------------------
// 3. ACCORDION ANIMATION ENHANCEMENT
// ---------------------------------------------------------------------------

/**
 * Accordion animation enhancement.
 *
 * CSS contract (lotusun-motion.css §3):
 *   - details.lu-accordion-enhanced  → grid container on <details>
 *   - details.lu-accordion-enhanced .lu-accordion-body → grid-template-rows 0fr (closed)
 *   - details.lu-accordion-enhanced[open] .lu-accordion-body → grid-template-rows 1fr (open)
 *   - CSS drives the transition entirely via native [open] attribute.
 *   - .lu-accordion-body must contain a single child with min-height:0 (CSS rule §3).
 *
 * JS responsibility:
 *   1. Add .lu-accordion-enhanced to each target <details>.
 *   2. Wrap the accordion body children in a .lu-accordion-body div if not present.
 *   3. Intercept close only: prevent default, wait for CSS close transition (180ms),
 *      then set details.open = false. Open uses native toggle — CSS [open] rule fires.
 *   4. Reduced-motion: skip interception entirely — native snap behavior.
 *
 * Target selector: <details> elements carrying the LotuSun accordion markup.
 * Liquid should render these as <details class="lotusun-accordion"> or similar;
 * JS targets them by a data attribute to remain selector-agnostic.
 * Fallback: if no data attribute is present, target details within the buy-box.
 */
function initAccordion() {
  // Primary selector: data-lotusun-accordion attribute (preferred Liquid convention)
  // Fallback: details inside the buy-box section
  let accordionItems = /** @type {NodeListOf<HTMLDetailsElement>} */ (
    document.querySelectorAll('details[data-lotusun-accordion]')
  );
  if (!accordionItems.length) {
    accordionItems = /** @type {NodeListOf<HTMLDetailsElement>} */ (
      document.querySelectorAll('.lotusun-buy-box details')
    );
  }
  if (!accordionItems.length) return;

  accordionItems.forEach((details) => {
    if (!(details instanceof HTMLDetailsElement)) return;

    // Step 1: Add the CSS opt-in class
    details.classList.add('lu-accordion-enhanced');

    // Step 2: Find or create .lu-accordion-body wrapper
    // The body is the content after <summary> — wrap it if not already wrapped.
    let body = details.querySelector(':scope > .lu-accordion-body');
    if (!body) {
      // Collect all non-summary direct children
      const contentNodes = Array.from(details.childNodes).filter(
        (node) => !(node instanceof HTMLElement && node.tagName === 'SUMMARY')
      );
      if (!contentNodes.length) return;

      body = document.createElement('div');
      body.className = 'lu-accordion-body';

      // Move content nodes into the wrapper
      contentNodes.forEach((node) => body.appendChild(node));
      details.appendChild(body);
    }

    // Step 3: Intercept close to allow CSS collapse transition to play.
    // DEFECT-2 fix: isClosing flag prevents stacked timeouts under rapid double-click.
    const summary = details.querySelector('summary');
    if (!summary) return;

    let isClosing = false;
    /** @type {ReturnType<typeof setTimeout> | null} */
    let closeTimer = null;

    summary.addEventListener('click', (event) => {
      // Reduced-motion: allow native instant toggle
      if (prefersReducedMotion()) return;

      if (!details.open) {
        // Opening: let native toggle run — CSS [open] rule fires the expand transition.
        // If a close animation is in flight, cancel it and let open proceed.
        if (isClosing) {
          isClosing = false;
          if (closeTimer !== null) {
            clearTimeout(closeTimer);
            closeTimer = null;
          }
        }
        return;
      }

      // Closing: if already closing, do not re-queue (prevents stacked timeouts).
      if (isClosing) return;

      // Closing: prevent native close, let CSS transition play (180ms), then close.
      event.preventDefault();
      isClosing = true;
      const duration = 180; // matches --lu-dur-accordion-close: 180ms
      closeTimer = setTimeout(() => {
        details.open = false;
        isClosing = false;
        closeTimer = null;
      }, duration);
    });
  });
}

// ---------------------------------------------------------------------------
// 4. AJAX ADD-TO-CART → HORIZON CART DRAWER
// ---------------------------------------------------------------------------

/**
 * Wire the LotuSun PDP buy-box form to Horizon's cart drawer via AJAX.
 *
 * Per spec §8.4 (G5.B):
 *   1. Intercept #lotusun-product-form submit
 *   2. POST to /cart/add.js via fetch
 *   3. On success: dispatch CartAddEvent (event name: 'cart:update') on document
 *      — Horizon's CartDrawerComponent listens for 'cart:update' and calls
 *        showDialog() when auto-open attribute is present
 *      — Horizon's CartIcon listens for 'cart:update' and updates count + pulse
 *   4. On fetch failure: remove preventDefault and let native POST proceed
 *      (progressive enhancement: no JS → form POSTs to /cart/add, redirects
 *       to cart page — the standard Shopify fallback)
 *
 * CartAddEvent.eventName confirmed as ThemeEvents.cartUpdate = 'cart:update'
 * (assets/events.js:115: static eventName = ThemeEvents.cartUpdate)
 *
 * itemCount passed in event detail.data.itemCount is read by CartIcon.onCartUpdate
 * to increment the displayed count (assets/cart-icon.js:57-60).
 *
 * source: 'product-form-component' tells CartIcon to ADD to existing count
 * rather than SET it (assets/cart-icon.js:74).
 *
 * Nav bag pulse: .cart-bubble.is-bumped is driven by this module after a
 * successful cart add. The class is added then removed after the animation
 * duration (300ms per --lu-dur-bag-pulse) to allow re-triggering.
 *
 * LotuSun voice: error message uses brand register — no "accessory", no
 * medical claims, no forbidden terms.
 */
function initCart() {
  const form = /** @type {HTMLFormElement | null} */ (
    document.getElementById('lotusun-product-form')
  );
  if (!form) return;

  /** @type {HTMLButtonElement | null} */
  let submitBtn = null;

  /** @param {SubmitEvent} event */
  async function handleCartSubmit(event) {
    event.preventDefault();

    submitBtn = form.querySelector('.lotusun-buy-box__add');
    if (submitBtn) {
      // native disabled is sufficient — aria-disabled is redundant on a <button>
      submitBtn.disabled = true;
    }

    let ajaxFailed = false;

    try {
      const formData = new FormData(form);
      // Shopify expects 'sections' param to get updated section HTML — omit here
      // since we let Horizon's cart drawer re-render via its own morph mechanism

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });

      if (!response.ok) {
        ajaxFailed = true;
        throw new Error('cart-add-failed');
      }

      const cartData = await response.json();

      // Dispatch CartAddEvent — Horizon's CartDrawerComponent and CartIcon
      // both listen for this event name ('cart:update').
      // CartIcon uses itemCount (quantity added) to increment the bubble count.
      // CartIcon uses source='product-form-component' to ADD (not SET) the count.
      const quantityAdded = cartData.quantity ?? 1;
      const cartAddEvent = new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cartData,
          sourceId: 'lotusun-product-form',
          data: {
            itemCount: quantityAdded,
            source: 'product-form-component',
          },
        },
      });
      document.dispatchEvent(cartAddEvent);

      // Nav bag pulse: add .is-bumped to .cart-bubble, remove after animation
      // CSS: .cart-bubble.is-bumped { animation: lu-bag-pulse 300ms ease-out forwards }
      const cartBubble = document.querySelector('.cart-bubble');
      if (cartBubble instanceof HTMLElement) {
        cartBubble.classList.remove('is-bumped');
        // Force reflow so removing then adding fires a fresh animation
        void cartBubble.offsetWidth;
        cartBubble.classList.add('is-bumped');
        setTimeout(() => {
          cartBubble.classList.remove('is-bumped');
        }, 320); // 300ms animation + 20ms buffer
      }
    } catch (_error) {
      if (ajaxFailed) {
        // On fetch/parse failure, remove our interceptor and fall back to native POST.
        // Native POST to /cart/add → Shopify redirects to cart page.
        // This preserves the progressive-enhancement contract.
        form.removeEventListener('submit', handleCartSubmit);
        form.submit();
        return;
      }
    } finally {
      // Re-enable button after a short delay (mirrors Horizon's ERROR_BUTTON_REENABLE_DELAY)
      setTimeout(() => {
        if (submitBtn) {
          submitBtn.disabled = false;
        }
      }, 1000);
    }
  }

  form.addEventListener('submit', handleCartSubmit);
}

// ---------------------------------------------------------------------------
// 5. QUANTITY STEPPER
// ---------------------------------------------------------------------------

/**
 * Quantity stepper ± buttons.
 *
 * Per spec §1 row 8: no animation. Value change is the feedback.
 * Guarded at min:1 (no negative quantity).
 *
 * Class B behavioral contract:
 *   - Decrement button is native [disabled] when value === min (persisted attribute).
 *   - Value changes are announced to AT via an aria-live="polite" status region.
 */
function initQtyStepper() {
  const form = document.getElementById('lotusun-product-form');
  if (!form) return;

  const qtyInput = /** @type {HTMLInputElement | null} */ (
    form.querySelector('#lotusun-qty')
  );
  if (!qtyInput) return;

  const minVal = parseInt(qtyInput.getAttribute('min') ?? '1', 10) || 1;

  // Locate the decrement button (data-qty-action="decrement")
  const decrementBtn = /** @type {HTMLButtonElement | null} */ (
    form.querySelector('[data-qty-action="decrement"]')
  );

  // Find or create polite live region for value announcements.
  // Placed outside the button group so AT reads it as a status update.
  let liveRegion = /** @type {HTMLElement | null} */ (
    form.querySelector('#lotusun-qty-live')
  );
  if (!liveRegion) {
    liveRegion = document.createElement('span');
    liveRegion.id = 'lotusun-qty-live';
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');
    // Visually hidden — for AT only.
    liveRegion.style.cssText =
      'position:absolute;width:1px;height:1px;padding:0;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0';
    form.appendChild(liveRegion);
  }

  /**
   * Sync boundary disabled state on the decrement button.
   * @param {number} value
   */
  function syncBoundaryState(value) {
    if (!decrementBtn) return;
    if (value <= minVal) {
      decrementBtn.disabled = true;
    } else {
      decrementBtn.disabled = false;
    }
  }

  // Set initial boundary state
  syncBoundaryState(parseInt(qtyInput.value, 10) || minVal);

  form.addEventListener('click', (event) => {
    const btn = /** @type {HTMLElement} */ (event.target).closest(
      '[data-qty-action]'
    );
    if (!(btn instanceof HTMLButtonElement)) return;

    const action = btn.dataset.qtyAction;
    const current = parseInt(qtyInput.value, 10) || minVal;

    let next = current;
    if (action === 'increment') {
      next = current + 1;
    } else if (action === 'decrement') {
      next = Math.max(minVal, current - 1);
    }

    qtyInput.value = String(next);
    syncBoundaryState(next);

    // Announce value change to AT
    if (liveRegion) {
      liveRegion.textContent = `Quantity: ${next}`;
    }

    // Dispatch input event so any listening components stay in sync
    qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
  });
}

// ---------------------------------------------------------------------------
// 6. MOBILE NAV DRAWER (MOB-NAV-001 / FOCUS-RETURN-001)
// ---------------------------------------------------------------------------

/**
 * Mobile nav drawer.
 *
 * Behavior contract:
 *   - Hamburger button (data-lotusun-hamburger) toggles the drawer.
 *   - Drawer: <nav id="lotusun-nav-drawer" class="lotusun-header__nav-drawer">
 *   - Scrim:  <div data-lotusun-scrim class="lotusun-header__nav-scrim">
 *   - Open:   adds .is-open to both drawer + scrim; removes [inert]; sets
 *             aria-expanded="true" on hamburger; locks body scroll.
 *   - Close:  removes .is-open from both; adds [inert]; sets
 *             aria-expanded="false"; unlocks body scroll; returns focus
 *             to the hamburger (WCAG 2.4.3 / FOCUS-RETURN-001).
 *   - Escape: closes drawer when drawer is open.
 *   - Scrim click: closes drawer.
 *   - Focus trap: Tab/Shift-Tab cycles within open drawer only.
 *   - Interruptible: instant class toggle per motion spec (no animation wait).
 *   - Drawer sits OUTSIDE the reveal system (no data-lotusun-reveal).
 *
 * Reduced-motion: CSS handles instant show/hide via
 * lotusun-motion.css §8.8 (transition: none !important on drawer + scrim).
 * JS logic is identical — CSS owns the visual path.
 *
 * No-JS: drawer is inert, hamburger has no binding. Desktop nav is still
 * visible ≥769px via CSS. On mobile without JS: no drawer, but the
 * desktop nav is hidden — that is the pre-JS baseline, same as before.
 */
function initMobileNavDrawer() {
  const hamburger = /** @type {HTMLButtonElement | null} */ (
    document.querySelector('[data-lotusun-hamburger]')
  );
  const drawer = /** @type {HTMLElement | null} */ (
    document.getElementById('lotusun-nav-drawer')
  );
  const scrim = /** @type {HTMLElement | null} */ (
    document.querySelector('[data-lotusun-scrim]')
  );
  const closeBtn = /** @type {HTMLButtonElement | null} */ (
    drawer ? drawer.querySelector('[data-lotusun-drawer-close]') : null
  );

  if (!hamburger || !drawer || !scrim) return;

  let isOpen = false;

  /**
   * SEAM_2 — MOB-FOCUSTRAP-001 (iteration 3): ancestor-chain sibling-inert.
   *
   * Root cause of prior iterations: the drawer is nested inside #header-group
   * alongside hamburger, desktop nav, wordmark, utilities, and cart-drawer-shell.
   * Inerting only body-level siblings (main, footer, etc.) left every element
   * inside the drawer's shared #header-group ancestor keyboard-reachable.
   *
   * Fix: walk from the drawer up to <body>, and at EVERY level inert each
   * SIBLING of the current node, exempting the drawer and scrim. Track every
   * element we set inert via data-lu-drawer-inert so removal is precise and
   * cannot accidentally remove inert from elements that were already inert.
   *
   * The scrim is exempt (not inerted) so tap-to-close still works — inert
   * kills pointer events.
   *
   * @param {Element} drawerEl
   * @param {Element} scrimEl
   * @returns {Element[]} list of elements we inerted (for clean removal)
   */
  function inertOutsideDrawer(drawerEl, scrimEl) {
    const exempt = new Set([drawerEl, scrimEl]);
    /** @type {Element[]} */
    const applied = [];
    let node = /** @type {Element | null} */ (drawerEl);
    while (node && node !== document.body && node.parentElement) {
      for (const sib of Array.from(node.parentElement.children)) {
        if (sib !== node && !exempt.has(sib) && !sib.hasAttribute('inert')) {
          sib.setAttribute('inert', '');
          sib.setAttribute('data-lu-drawer-inert', '');
          applied.push(sib);
        }
      }
      node = node.parentElement;
    }
    return applied;
  }

  /** Elements we inerted on open — cleared on close. @type {Element[]} */
  let inertedElements = [];

  /** Open the drawer. */
  function openDrawer() {
    isOpen = true;
    drawer.classList.add('is-open');
    scrim.classList.add('is-open');
    drawer.removeAttribute('inert');
    drawer.removeAttribute('aria-hidden');
    hamburger.setAttribute('aria-expanded', 'true');

    // MOB-FOCUSTRAP-001: ancestor-chain sibling-inert — inerts every element
    // outside the drawer's ancestor path so focus is fully contained.
    inertedElements = inertOutsideDrawer(drawer, scrim);

    // Lock body scroll
    document.body.style.overflow = 'hidden';

    // Move focus to the close button (first interactive element in drawer)
    if (closeBtn) {
      closeBtn.focus();
    } else {
      const firstFocusable = /** @type {HTMLElement | null} */ (
        drawer.querySelector('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')
      );
      if (firstFocusable) firstFocusable.focus();
    }

    // Escape handler while open
    document.addEventListener('keydown', handleDrawerKeyDown);
  }

  /** Close the drawer and return focus to hamburger (FOCUS-RETURN-001). */
  function closeDrawer() {
    if (!isOpen) return;
    isOpen = false;
    drawer.classList.remove('is-open');
    scrim.classList.remove('is-open');
    drawer.setAttribute('inert', '');
    drawer.setAttribute('aria-hidden', 'true');
    hamburger.setAttribute('aria-expanded', 'false');

    // MOB-FOCUSTRAP-001: remove inert from exactly the elements we set it on.
    // Use the data-lu-drawer-inert marker for precise, order-independent removal.
    // Must run BEFORE hamburger.focus() so the hamburger is interactive.
    document.querySelectorAll('[data-lu-drawer-inert]').forEach((el) => {
      el.removeAttribute('inert');
      el.removeAttribute('data-lu-drawer-inert');
    });
    inertedElements = [];

    // Unlock body scroll
    document.body.style.overflow = '';

    document.removeEventListener('keydown', handleDrawerKeyDown);

    // FOCUS-RETURN-001: return focus to hamburger on close
    hamburger.focus();
  }

  /**
   * Query all focusable elements inside the drawer (in DOM order).
   * Excludes elements that are inert or have tabindex="-1".
   * @returns {HTMLElement[]}
   */
  function getDrawerFocusables() {
    return Array.from(
      drawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter(
      /** @param {Element} el */
      (el) => !el.closest('[inert]') || el.closest('[inert]') === drawer
    );
  }

  /**
   * Keyboard handler while drawer is open.
   * - Escape: close drawer.
   * - Tab / Shift+Tab: explicit wrap to prevent browser from cycling through
   *   document.body between the last and first drawer focusables (Chromium
   *   wrap-via-body behavior when all body-level siblings are inert).
   */
  function handleDrawerKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeDrawer();
      return;
    }

    if (event.key === 'Tab') {
      const focusables = getDrawerFocusables();
      if (focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (event.shiftKey) {
        // Shift+Tab on first element → wrap to last
        if (document.activeElement === first) {
          event.preventDefault();
          last.focus();
        }
      } else {
        // Tab on last element → wrap to first
        if (document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }
  }

  // Wire hamburger toggle
  hamburger.addEventListener('click', () => {
    if (isOpen) {
      closeDrawer();
    } else {
      openDrawer();
    }
  });

  // Wire close button inside drawer
  if (closeBtn) {
    closeBtn.addEventListener('click', closeDrawer);
  }

  // Wire scrim click
  scrim.addEventListener('click', closeDrawer);

  // Close on resize to desktop (drawer should not persist above 768px)
  window.addEventListener('resize', () => {
    if (isOpen && window.innerWidth > 768) {
      closeDrawer();
    }
  });
}

// ---------------------------------------------------------------------------
// 7. DOCUMENTARY-FEATURE THREE-BEAT REVEAL
// ---------------------------------------------------------------------------

/**
 * Documentary-feature three-beat reveal.
 *
 * Spec authority: MOTION-phase09-story-mission.md Part III
 *
 * Separate from initScrollReveal() — uses data-lotusun-documentary-reveal
 * attribute, NOT data-lotusun-reveal, so the existing reveal system leaves
 * it alone.
 *
 * Three-beat sequence (CSS-driven via .is-revealed toggle):
 *   Beat 1 (t=0ms)    — .lu-doc-image  clip-path + scale, 600ms
 *   Beat 2 (t=200ms)  — .lu-doc-rule   scaleX draw, 300ms
 *   Beat 3 (t=400ms)  — .lu-doc-overlay opacity + translateY, 300ms
 *
 * FOUC safety: hidden states in §9 are gated on html.lu-reveal-ready.
 * Without that class (no-JS or reduced-motion path), elements are fully
 * visible — this function never hides anything.
 *
 * Reduced-motion path: if prefersReducedMotion() is true, .is-revealed
 * is added synchronously (no requestAnimationFrame) so the CSS §9
 * reduced-motion block instantly displays all elements.
 *
 * IntersectionObserver threshold: 0.18 per MOTION-phase09 Part III.
 */
function initDocumentaryReveal() {
  const elements = document.querySelectorAll('[data-lotusun-documentary-reveal]');
  if (!elements.length) return;

  // Reduced-motion: add .is-revealed synchronously — no animation frame,
  // no observer. CSS §9 reduced-motion block handles instant display.
  if (prefersReducedMotion()) {
    elements.forEach((el) => el.classList.add('is-revealed'));
    return;
  }

  // If lu-reveal-ready is absent (no-JS or IO unsupported), elements are
  // already fully visible — nothing to do.
  if (!document.documentElement.classList.contains('lu-reveal-ready')) return;

  try {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          // Add .is-revealed — CSS §9 transitions fire the three-beat cascade.
          el.classList.add('is-revealed');
          observer.unobserve(el);
        });
      },
      { threshold: 0.18 }
    );

    elements.forEach((el) => observer.observe(el));
  } catch (_err) {
    // If observer setup throws after lu-reveal-ready is set, reveal all
    // immediately so no element is left hidden.
    elements.forEach((el) => el.classList.add('is-revealed'));
  }
}

// ---------------------------------------------------------------------------
// ENTRY POINT
// ---------------------------------------------------------------------------

/**
 * Initialize all LotuSun interaction behaviors.
 * Scoped entirely to LotuSun elements — no Horizon globals touched.
 */
function init() {
  initScrollReveal();
  initDocumentaryReveal();
  initGallery();
  initAccordion();
  initCart();
  initQtyStepper();
  initMobileNavDrawer();
}

// DOMContentLoaded guard — script is loaded as type=module with defer,
// so DOM is parsed; use the event as a safe fallback.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
